import { Button, Form, Select, Switch } from 'antd'
import { FormInstance } from 'antd/lib'

import Loading from '@/components/Loading'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import { useLoadCustomerFieldsTypes } from '@/queries/customerFields/useLoadCustomerFieldsTypes'
import FormAntd from '@/ui/form/antd/FormAntd'
import { getFilterOption } from '@/utils/getFilterOption'

import {
  CustomFieldsFormModel,
  useCustomFieldsForm,
} from './useCustomFieldsForm'

type Props = {
  initialValues?: CustomFieldsFormModel
  onFormSubmit: (
    data: CustomFieldsFormModel,
    form?: FormInstance<CustomFieldsFormModel>
  ) => void
  isPending: boolean
  isNew?: boolean
}

const DEFAULT_INITIAL_VALUES = {
  show_in_client_mapping: true,
}

export const CustomFieldsForm = ({
  initialValues,
  onFormSubmit,
  isPending,
  isNew,
}: Props) => {
  const { form, rule } = useCustomFieldsForm()
  const { data: customerFieldsTypes, isLoading: isLoadingCustomerFieldsTypes } =
    useLoadCustomerFieldsTypes()
  const updateParams = useUpdateURLSearchParams()
  const onCancel = () =>
    updateParams(['addNew', undefined], ['editId', undefined])

  const hasInitialValues = Object.keys(initialValues || {}).length

  const filteredCustomerFieldsTypes = customerFieldsTypes?.filter(
    (fieldType) => {
      return !(
        fieldType.value.startsWith('checkbox') ||
        fieldType.value.startsWith('radio')
      )
    }
  )

  let buttonSubmitText = 'Create'
  if (hasInitialValues) buttonSubmitText = 'Save'

  if (isPending) return <Loading className="!h-60" />

  return (
    <FormAntd.Root<CustomFieldsFormModel>
      form={form}
      initialValues={initialValues || DEFAULT_INITIAL_VALUES}
      layout="vertical"
      onFinish={(data) => onFormSubmit?.(data, form)}
      validateTrigger={['onBlur']}
    >
      <Form.Item<CustomFieldsFormModel>
        required
        className="mt-4 w-full"
        label="Field Name"
        name="label"
        rules={[rule]}
      >
        <FormAntd.Input placeholder="Enter field name" type="text" />
      </Form.Item>

      <Form.Item<CustomFieldsFormModel>
        required
        className="mt-2 w-full"
        label="Field Type"
        name="type"
        rules={[rule]}
      >
        <Select
          allowClear
          showSearch
          disabled={!isNew}
          filterOption={(input, option) => getFilterOption(input, option)}
          loading={isLoadingCustomerFieldsTypes}
          notFoundContent="No options found"
          options={filteredCustomerFieldsTypes}
          placeholder="Select a type"
        />
      </Form.Item>

      <Form.Item<CustomFieldsFormModel>
        className="mt-3 w-full [&_.ant-form-item-control]:flex [&_.ant-form-item-control]:items-end [&_label]:after:hidden"
        label="Show in Client Mapping"
        layout="horizontal"
        name="show_in_client_mapping"
      >
        <Switch className="focus:outline-none" />
      </Form.Item>

      <div className="-mb-2 -ml-6 flex w-[calc(100%+48px)] justify-end gap-4 pt-4">
        <Button onClick={onCancel} type="default">
          Cancel
        </Button>
        <Button className="mr-6" htmlType="submit" type="primary">
          {buttonSubmitText}
        </Button>
      </div>
    </FormAntd.Root>
  )
}
